import type { ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useAuth } from './AuthProvider';
import { landingFor, shopIdsFor, isPlatformAdmin, shopPath, shopSlugFor } from '../lib/roles.js';
import { AuthPage, EmptyState, Loading, useTitle } from '../ui/bits';
import { STATE_ART } from '../lib/voice.js';
import { Icon } from '../ui/Icon';

function Splash() {
  return (
    <AuthPage card={false}>
      <Loading label="Opening the back office" />
    </AuthPage>
  );
}

/** Signed in, and allowed by `allow` (else the polite no-access page). */
export function RequireAuth({ allow, children }: { allow?: (me: NonNullable<ReturnType<typeof useAuth>['me']>) => boolean; children: ReactNode }) {
  const { status, me } = useAuth();
  const loc = useLocation();
  if (status === 'loading') return <Splash />;
  if (status === 'signedOut' || !me) return <Navigate to="/sign-in" replace state={{ from: loc.pathname }} />;
  if (allow && !allow(me)) return <Navigate to={landingFor(me).kind === 'denied' ? '/no-access' : landingFor(me).path} replace />;
  return <>{children}</>;
}

/** "/" → wherever this user belongs. */
export function Landing() {
  const { status, me } = useAuth();
  if (status === 'loading') return <Splash />;
  if (!me) return <Navigate to="/sign-in" replace />;
  return <Navigate to={landingFor(me).path} replace />;
}

export function NoAccess() {
  useTitle('No access');
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <AuthPage>
      <EmptyState art={STATE_ART.noAccess} title="No back-office access yet">
        This account doesn't have back-office access yet. Ask PopCart Ops to add you.
        {me?.email ? ` You're signed in as ${me.email}.` : ''}
      </EmptyState>
      <button
        type="button"
        className="btn"
        onClick={async () => {
          await signOut();
          navigate('/sign-in', { replace: true });
        }}
      >
        <Icon name="logout" />
        Sign out and use another account
      </button>
    </AuthPage>
  );
}

/** For people who work at more than one shop (and admins switching shops). */
export function ShopPicker() {
  useTitle('Choose a shop');
  const { me } = useAuth();
  const ids = shopIdsFor(me);
  const shops = useQuery({ queryKey: ['shops', 'public'], queryFn: () => api.shops(), staleTime: 60_000 });
  const nameOf = (id: string) => shops.data?.find((s) => s.id === id)?.name ?? 'Shop';
  return (
    <AuthPage>
      <h1 className="t-headline">Which shop are you working at?</h1>
      {shops.isLoading ? <Loading label="Loading shops" /> : null}
      <ul className="stack">
        {ids.map((id) => (
          <li key={id}>
            <Link className="btn" style={{ width: '100%', justifyContent: 'space-between' }} to={shopPath(shopSlugFor(me, id), id)}>
              <span>{nameOf(id)}</span>
              <Icon name="arrow" />
            </Link>
          </li>
        ))}
      </ul>
      {isPlatformAdmin(me) ? (
        <Link className="btn btn-quiet" to="/ops">
          Back to Ops
        </Link>
      ) : null}
    </AuthPage>
  );
}
